import {
  Box,
  Flex,
  Grid,
  GridItem,
  HStack,
  NativeSelect,
  Text,
} from '@chakra-ui/react'
import React, { useMemo } from 'react'

import { useReportingI18n } from '../../../i18n'
import { SAMPLE_HISTORY_OPTIONS_WITH_LABELS } from '../constants'
import type { QueueDashboardSettings } from '../types'
import { QueueStats } from '../types'
import MetricCard from './MetricCard'

const getFormattedTimeDigits = (digits: number): string => {
  return `${digits < 10 ? '0' : ''}${digits}`
}

const formatSeconds = (seconds: number): string => {
  const hours = (seconds / 3600) | 0
  let remainingSeconds = seconds - hours * 3600
  const minutes = (remainingSeconds / 60) | 0
  remainingSeconds = (remainingSeconds - minutes * 60) | 0

  if (hours > 0) {
    return `${getFormattedTimeDigits(hours)}:${getFormattedTimeDigits(minutes)}:${getFormattedTimeDigits(
      remainingSeconds,
    )}`
  }

  return `${getFormattedTimeDigits(minutes)}:${getFormattedTimeDigits(
    remainingSeconds,
  )}`
}

export interface QueueMetricsCardsProps {
  stats: QueueStats | null
  callersInQueue: number
  isLoading: boolean
  isLoadingCallers?: boolean
  settings?: QueueDashboardSettings
  onSettingsChange?: (settings: Partial<QueueDashboardSettings>) => void
}

const QueueMetricsCards: React.FC<QueueMetricsCardsProps> = ({
  stats,
  callersInQueue,
  isLoading,
  isLoadingCallers = false,
  settings,
  onSettingsChange,
}) => {
  const { text } = useReportingI18n()
  const handleSampleHistoryChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = parseInt(e.target.value, 10)
      if (!isNaN(value) && onSettingsChange) {
        onSettingsChange({ sampleHistoryMinutes: value })
      }
    },
    [onSettingsChange],
  )
  const metrics = useMemo(() => {
    if (!stats) {
      return {
        maxWait: formatSeconds(0),
        avgWait: formatSeconds(0),
        maxAbandoned: formatSeconds(0),
        avgAbandoned: formatSeconds(0),
        dropRate: '0%',
        answerRate: '0%',
      }
    }

    const answerRate =
      stats.answeredCount + stats.cancelledCount > 0
        ? Math.round(
            (stats.answeredCount /
              (stats.answeredCount + stats.cancelledCount)) *
              100,
          )
        : 0

    return {
      maxWait: formatSeconds(Math.round(stats.maxWaitSeconds)),
      avgWait: formatSeconds(Math.round(stats.avgWaitSeconds)),
      maxAbandoned: formatSeconds(Math.round(stats.maxCancelledSeconds)),
      avgAbandoned: formatSeconds(Math.round(stats.avgCancelledSeconds)),
      dropRate: `${Math.round(stats.dropRatePercent)}%`,
      answerRate: `${answerRate}%`,
    }
  }, [stats])

  return (
    <Flex direction="column" gap="16px" width="100%">
      {settings && onSettingsChange && (
        <Flex justifyContent="flex-end" width="100%">
          <HStack gap={1}>
            <Text fontSize="0.75rem" color="fg.muted" whiteSpace="nowrap">
              {text('queueDashboard.metrics.sampleHistory', 'Sample History')}
            </Text>
            <NativeSelect.Root size="xs" width="80px">
              <NativeSelect.Field
                value={settings.sampleHistoryMinutes}
                onChange={handleSampleHistoryChange}
                aria-label={text(
                  'queueDashboard.metrics.sampleHistoryAriaLabel',
                  'Select sample history time window',
                )}
                data-testid="sample-history-select"
                fontSize="0.75rem"
                bg="bg.default"
              >
                {SAMPLE_HISTORY_OPTIONS_WITH_LABELS.map((option) => {
                  const minutes = option.minutes
                  let label: string
                  if (minutes < 60) {
                    label = text(
                      'queueDashboard.settings.minutes',
                      '{{count}} min',
                      {
                        count: minutes,
                      },
                    )
                  } else {
                    const hours = minutes / 60
                    label = text(
                      'queueDashboard.settings.hours',
                      '{{count}} hr',
                      {
                        count: hours,
                      },
                    )
                  }
                  return (
                    <option key={option.value} value={option.value}>
                      {label}
                    </option>
                  )
                })}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </HStack>
        </Flex>
      )}

      <Flex
        gap="16px"
        width="100%"
        direction="column"
        css={{
          // Container query: switch to row layout when container is wide enough
          '@container queue-dashboard (min-width: 500px)': {
            flexDirection: 'row',
          },
        }}
      >
        <Box
          bg="bg.default"
          borderRadius="8px"
          border="1px solid"
          borderColor="border.default"
          padding="16px"
          minWidth="140px"
          display="flex"
          flexDirection="column"
        >
          <Text fontSize="0.75rem" fontWeight="500" color="fg.muted">
            {text('queueDashboard.metrics.callersInQueue', 'Callers In Queue')}
          </Text>
          <Flex flex="1" alignItems="center" justifyContent="center">
            <Text
              fontSize="5rem"
              fontWeight="700"
              lineHeight="1"
              color="blue.solid"
              data-testid="callers-in-queue-count"
            >
              {isLoadingCallers ? '-' : callersInQueue}
            </Text>
          </Flex>
        </Box>

        <Grid
          templateColumns="repeat(2, 1fr)"
          templateRows="repeat(3, 1fr)"
          gap="12px"
          flex="1"
          css={{
            // Container query: switch to 3 columns when container is wide enough
            '@container queue-dashboard (min-width: 700px)': {
              gridTemplateColumns: 'repeat(3, 1fr)',
              gridTemplateRows: 'repeat(2, 1fr)',
            },
          }}
        >
          <GridItem>
            <MetricCard
              label={text('queueDashboard.metrics.answerRate', 'Answer Rate')}
              value={metrics.answerRate}
              isLoading={isLoading}
            />
          </GridItem>
          <GridItem>
            <MetricCard
              label={text('queueDashboard.metrics.maxWait', 'Max Wait')}
              value={metrics.maxWait}
              isLoading={isLoading}
            />
          </GridItem>

          <GridItem>
            <MetricCard
              label={text(
                'queueDashboard.metrics.maxAbandoned',
                'Max Abandoned',
              )}
              value={metrics.maxAbandoned}
              isLoading={isLoading}
              valueColor="red.solid"
            />
          </GridItem>
          <GridItem>
            <MetricCard
              label={text(
                'queueDashboard.metrics.averageAbandoned',
                'Average Abandoned',
              )}
              value={metrics.avgAbandoned}
              isLoading={isLoading}
            />
          </GridItem>

          <GridItem>
            <MetricCard
              label={text('queueDashboard.metrics.dropRate', 'Drop Rate')}
              value={metrics.dropRate}
              isLoading={isLoading}
              valueColor="red.solid"
            />
          </GridItem>
          <GridItem>
            <MetricCard
              label={text('queueDashboard.metrics.averageWait', 'Average Wait')}
              value={metrics.avgWait}
              isLoading={isLoading}
            />
          </GridItem>
        </Grid>
      </Flex>
    </Flex>
  )
}

export default QueueMetricsCards
